/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import isfunction from 'lodash.isfunction';
import {getEnvironment} from '../environment/environment';
import {entityId} from '../services/ConnectUtils';
import {GenericObject, Link, User} from '../types/entities';
import {
  logInitializeError,
  logInitializeInjectError,
  logInitializeSuccess,
  logInjectAPIKeyError,
  logInjectAvailableError,
  logInjectDependencyError,
  logInjectError,
  logInjectSuccess,
  logPendoValidation,
  logTrackError,
  logTrackSuccess
} from './PendoLogger';

declare const window: any;
declare const globalThis: any;

/**
 * The Pendo API provides the single point of entry for all Pendo.io integrations
 * and configuration. The process of integrating Pendo within iOn Applications is
 * as follows:
 *
 * 1.) Obtain Pendo apiKey (generally from Product Team)
 * 2.) Include the pendo installation script (see ion-suite/src/public/vendor/pendo.js)
 *     for a reference implementation.
 * 3.) Invoke Pendo.injectScript(your-api-key)
 * 4.) Upon successful user authentication, invoke Pendo.initialize(user)
 *
 * ```typescript
 *
 *    import Pendo from '@ion/core/src/analytics/Pendo';
 *
 *    // invoke script injection once api key is available ...
 *    Pendo.injectScript(apiKey);
 *
 *    // invoke user initialization upon successful login ...
 *    Pendo.initialize(apiKey, window || globalThis);
 *
 * ```
 */
class Pendo {
  /**
   * @private
   */
  static _preinitialized = false;

  /**
   *
   * ```typescript
   *
   *   import Pendo from '@ion/core/src/analytics/Pendo';
   *
   *   // upon successful user authentication ...
   *   Pendo.initialize(user, {application: 'ion-suite'});
   *
   * ```
   *
   * @see https://support.pendo.io/hc/en-us/articles/360046272771-Developer-s-Guide-To-Installing-Pendo#TheInitialization
   *
   * @param user The authenticated CTC User entity against which to track.
   * @param options An object of arbitrary options to pass to pendo during
   *        initialization. Note, these options are semi-arbitrary in that
   *        they must not contain overrides of the reserved pendo configurations.
   */
  static initialize(user: User, options?: GenericObject) {
    if (Pendo._preinitialized) {
      const initializer = window.pendo?.initialize;
      if (isfunction(initializer)) {
        initializer({
          visitor : createVisitor(user, options),
          account : createAccount(user.account)
        });
        logInitializeSuccess();
        logPendoValidation();
        return true;
      } else {
        logInitializeError();
      }
    } else {
      logInitializeInjectError();
    }
    return false;
  }

  /**
   * Provides the required setup configuration for integration with the Pendo API and
   * platform. The injectScript method must be invoked prior to any subsequent calls
   * in order to initialize the pendo script against the applications API Key.
   *
   * ```typescript
   *
   *   import Pendo from '@ion/core/src/analytics/Pendo';
   *
   *   Pendo.injectScript(config.pendoAPIKey, globalThis);
   *
   * ```
   *
   * @param apiKey An optional Pendo API key for the application. By default, the API
   *        Key value will be set to that of the build time `process.env.PENDO_API_KEY`
   *        value, and the `apiKey` argument will only be used in the event that the
   *        `process.env.PENDO_API_KEY` value is not set.
   * @param window A reference to the window object to which the pendo dependency script
   *               has been added.
   */
  static injectScript(apiKey: string, window = globalThis) {
    if (!Pendo._preinitialized) {
      const key = getPendoApiKey() || apiKey;
      if (key) {
        const injector = window.__initializeAnalytics;
        if (isfunction(injector)) {
          try {
            injector(key);
            logInjectSuccess();
            return (Pendo._preinitialized = true);
          } catch (err) {
            logInjectError();
          }
        } else {
          logInjectDependencyError();
        }
      } else {
        logInjectAPIKeyError();
      }
    } else {
      logInjectAvailableError();
    }
    return false;
  }

  /**
   * Tracks an application specific event.
   */
  static track(action, obj): boolean {
    if (Pendo._preinitialized) {
      const track = window.pendo.track;
      if (isfunction(track)) {
        obj.environment = getEnvironment();
        track(action, obj);
        logTrackSuccess();
        return true;
      } else {
        logTrackError();
      }
    }
    return false;
  }
};

/**
 * @private
 *
 * Defines the static, build-time configuration for the Pendo API key value.
 * This value is used as the default API Key when `injectScript` is invoked,
 * taking precedence over the `apiKey` argument, and only setting the API Key
 * to the `apiKey` argument value if `process.env.NEXT_PUBLIC_PENDO_API_KEY`
 * has not been defined.
 */
const getPendoApiKey = (): string => process.env.NEXT_PUBLIC_PENDO_API_KEY;

/**
 * Provides a convenience function which creates the visitor pendo configuration
 * based on the provided user and optional tracking parameters.
 */
const createVisitor = (user: User, options) => {
  const {id, username, email, roles, firstName, lastName} = user;
  return {
    ...options,
    full_name   : `${firstName} ${lastName}`,
    role        : roles.map(r => r?.title)?.toString(),
    environment : getEnvironment(),
    username,
    email,
    id
  };
};

/**
 * Provides a convenience function which creates the account pendo configuration
 * based on the provided user account.
 */
const createAccount = ({href, title: name}: Link) => ({
  id : entityId(href),
  name
});

/**
 * @private
 *
 * Provides a mean of removing the pendo global and subsequent tracking api.
 * This method should be used with caution and only invoked in rare cases where
 * the  API should be disabled and removed entirely, such as if the underlying
 * service is unavailable or fails for some reason.
 */
export const __reset = () => {
  delete window.pendo;
  delete window.__initializeAnalytics;
  Pendo._preinitialized = false;
};

export default Pendo;
